import { Button } from '@sparemin/blockhead';
import ActionItemsList from 'components/ActionItemsList';
import TextButton from 'components/TextButton';
import { useCallback } from 'react';
import { useEventTracking } from 'state/eventTracking';
import { useCreditsBalance, useIsProGrow } from 'state/pricing';
import { getCheckoutAttribution } from 'utils/attribution';
import { dollarsToMillicents } from 'utils/money';
import { useCampaignCreation } from '../CampaignCreationContext';
import { getContentTarget } from '../CampaignCreationContext/utils';
import PageHeading from '../PageHeading';
import { useCreateAdCampaign } from '../state';
import CreditsSummary from './CreditsSummary';
import ReviewItem from './ReviewItem';
import * as S from './styles';
import useReviewItems from './useReviewItems';
import { getSubtotal } from './utils';

export interface ReviewViewProps {
  onGoBack: () => void;
  onSuccess: () => void;
  onCancel: () => void;
}

const ReviewView: React.FC<ReviewViewProps> = ({
  onGoBack,
  onSuccess,
  onCancel,
}) => {
  const { trackPodcastPromoPurchaseNext } = useEventTracking();
  const { mutateAsync: createCampaign, isLoading } = useCreateAdCampaign();
  const {
    state: { values, isCustomBudget },
  } = useCampaignCreation();
  const { data: isProGrow } = useIsProGrow();
  const { data: creditsBalance } = useCreditsBalance();

  const { remaining = 0 } = creditsBalance ?? {};
  const subtotal = getSubtotal(isCustomBudget, values.campaignBudgetMilliCent);
  const totalDue = subtotal - remaining;

  const reviewItems = useReviewItems({ onGoBack });

  const handleSubmit = useCallback(async (): Promise<void> => {
    trackPodcastPromoPurchaseNext({
      step: 'Review',
      buttonClick: 'ContinueToPurchase',
    });

    const { episode, ...campaignValues } = values;

    const { checkoutUrl, isCheckoutRequired } = await createCampaign({
      ...campaignValues,
      targetCategories: values?.targetCategories?.includes('General Interest')
        ? null
        : values.targetCategories,
      targetCountryCodes: values?.targetCountryCodes?.includes('Worldwide')
        ? null
        : values.targetCountryCodes,
      ...(episode && getContentTarget(episode)),
      campaignBudgetMilliCent: isCustomBudget
        ? dollarsToMillicents(Number(values?.campaignBudgetMilliCent))
        : values.campaignBudgetMilliCent,
      isTargetingContactRequest: values?.isTargetingContactRequest ?? false,
      checkoutAttribution: getCheckoutAttribution(),
    });

    if (isCheckoutRequired) {
      window.location.href = checkoutUrl;
    } else {
      onSuccess();
    }
  }, [
    createCampaign,
    isCustomBudget,
    onSuccess,
    trackPodcastPromoPurchaseNext,
    values,
  ]);

  return (
    <S.Root>
      <PageHeading title="Let’s do a quick review of your campaign!" />

      <S.ReviewItemsContainer orientation="vertical" space="16px">
        <ActionItemsList>
          {reviewItems.map((item) => (
            <ReviewItem key={item.id} isDisabled={isLoading} {...item} />
          ))}
        </ActionItemsList>

        {isProGrow && <CreditsSummary {...{ subtotal, remaining, totalDue }} />}
      </S.ReviewItemsContainer>

      <Button fluid isDisabled={isLoading} onPress={handleSubmit}>
        {totalDue <= 0 ? 'purchase' : 'continue to purchase'}
      </Button>

      <TextButton onPress={onCancel}>cancel</TextButton>
    </S.Root>
  );
};

export default ReviewView;
